import { inject, Injectable } from '@angular/core';
import { datadogLogs } from '@datadog/browser-logs';
import { Config } from '@luggagehero/shared/environment';
import { SharedWindowService } from '@luggagehero/shared/services/window';

import { LogEvent, LogLevel, LogTargetBase } from './log.target';

@Injectable()
export class DataDogTarget extends LogTargetBase {
  private windowService = inject(SharedWindowService);

  private static initialized = false;

  writeToLog(event: LogEvent) {
    if (Config.isDevelopment) {
      console.dir(event);
      return Promise.resolve();
    }

    if (!DataDogTarget.initialized) {
      this.initializeDatadog();
      DataDogTarget.initialized = true;
    }

    switch (event.level) {
      case LogLevel.Debug:
        datadogLogs.logger.debug(event.message, event.context, event.error);
        break;
      case LogLevel.Info:
        datadogLogs.logger.info(event.message, event.context, event.error);
        break;
      case LogLevel.Warning:
        datadogLogs.logger.warn(event.message, event.context, event.error);
        break;
      case LogLevel.Error:
        datadogLogs.logger.error(event.message, event.context, event.error);
        break;
    }

    return Promise.resolve();
  }

  private initializeDatadog() {
    datadogLogs.init({
      clientToken: Config.environment.DATADOG_CLIENT_TOKEN ?? '',
      site: Config.environment.DATADOG_SITE ?? '',
      forwardErrorsToLogs: false, // Enable after we have received angular errors in datadog
      service: this.windowService.appName,
      version: this.windowService.appVersion,
      env: Config.environment.ENV,
      // Defence-in-depth: drop Angular Router `NoMatch` sentinels at the SDK
      // boundary so they never reach Datadog regardless of how they arrive
      // (app ErrorHandler, or the SDK directly once forwardErrorsToLogs is on).
      // They are benign internal control-flow throws with an empty message,
      // identified by the router recognizer frames in the stack. See AM-4393.
      beforeSend: (event) => {
        const stack = event.error?.stack ?? '';
        if (stack.includes('processSegmentAgainstRoute') || stack.includes('expandSegmentAgainstRouteUsingRedirect')) {
          return false;
        }
        return true;
      },
    });
  }
}
